import { DocumentTypeLabel } from '../../../../types/testType';
import type {
  DocumentData,
  CategoryDataMap,
  ArticleType,
  TopicPageAppPropertiesType,
  TestReferenceComponent,
  BenefitOverviewType,
  QueryParam,
} from './useDocumentUrl.types';

const SIMPLE_SLUG_ROUTES: Partial<Record<DocumentTypeLabel, string>> = {
  [DocumentTypeLabel.faq]: '/kundeservice',
  [DocumentTypeLabel.myNafSubPage]: '/mitt-naf',
  [DocumentTypeLabel.localDepartment]: '/her-finner-du-naf/lokalavdelinger',
  [DocumentTypeLabel.mcClub]: '/her-finner-du-naf/mc-klubber',
  [DocumentTypeLabel.nafPracticeTrack]: '/her-finner-du-naf/naf-ovingsbaner',
  [DocumentTypeLabel.vehicleDataModel]: '/bilguiden/bilmodell',
  [DocumentTypeLabel.nafCenterArticle]: '/bilverksted-og-tester/naf-senter',
};

const isArticleType = (data: DocumentData): data is ArticleType =>
  data.type === DocumentTypeLabel.nafnoArticle || data.type === DocumentTypeLabel.nafnoNewsArticle;

const resolveCategoryUrl = (
  data: ArticleType | TopicPageAppPropertiesType | TestReferenceComponent,
  categories: CategoryDataMap = {},
): string | undefined => {
  if (data.parent?.type === DocumentTypeLabel.nafCenterProductArticle) {
    return `/bilverksted-og-tester/${data.parent.slug}/${data.slug}`;
  }

  const ownCategory = data.category?.slug ? categories[data.category.slug]?.data : null;
  const parentCategory =
    !ownCategory && data.parent?.category?.slug ? categories[data.parent.category.slug]?.data : null;

  if (parentCategory) {
    return `${parentCategory.url}/${data.parent?.slug}/${data.slug}`;
  }

  if (ownCategory) {
    return `${ownCategory.url}/${data.slug}`;
  }

  return undefined;
};

const resolveArticleUrl = (data: ArticleType, categories: CategoryDataMap = {}): string | undefined => {
  const parentType = data.parent?.type;

  if (parentType === DocumentTypeLabel.nafMembershipBenefit) {
    return `/medlemskap/medlemsfordeler/${data.parent.slug}/${data.slug}`;
  }

  if (parentType === DocumentTypeLabel.nafnoProductArticle) {
    if (data.parent?.slug === 'medlemskap') {
      return `/medlemskap/${data.slug}`;
    }
    return `/medlemskap/${data.parent.slug}/${data.slug}`;
  }

  if (parentType === DocumentTypeLabel.nafCenterProductArticle) {
    return `/bilverksted-og-tester/${data.parent.slug}/${data.slug}`;
  }

  if (parentType === DocumentTypeLabel.localDepartment) {
    return `/her-finner-du-naf/lokalavdelinger/${data.parent.slug}/artikkel/${data.slug}`;
  }

  if (parentType === DocumentTypeLabel.mcClub) {
    return `/her-finner-du-naf/mc-klubber/${data.parent.slug}/artikkel/${data.slug}`;
  }

  return resolveCategoryUrl(data, categories);
};

export const resolveDocumentUrl = (
  documentData?: DocumentData,
  categoryData: CategoryDataMap = {},
): string | undefined => {
  if (!documentData) {
    return undefined;
  }

  const { type, slug } = documentData;

  // Simple slug-based routes
  const simpleRoute = SIMPLE_SLUG_ROUTES[type];
  if (simpleRoute) {
    return `${simpleRoute}/${slug}`;
  }

  // Simple /<slug> types
  if (
    type === DocumentTypeLabel.abTestExperiment ||
    type === DocumentTypeLabel.myNafFrontpage ||
    type === DocumentTypeLabel.legalDocument
  ) {
    return `/${slug}`;
  }

  if (type === DocumentTypeLabel.travelSuggestion) {
    const parentUrl = categoryData[documentData.parent.slug]?.data?.url;
    return `${parentUrl}/${slug}`;
  }

  if (isArticleType(documentData)) {
    return resolveArticleUrl(documentData, categoryData);
  }

  if (type === DocumentTypeLabel.benefitOverview) {
    return `/medlemskap/medlemsfordeler?${(documentData as BenefitOverviewType).parameters}`;
  }

  if (
    type === DocumentTypeLabel.articleRangeReferenceComponent ||
    type === DocumentTypeLabel.articleChargeReferenceComponent
  ) {
    return `/bilguiden/bilmodell/${(documentData as TestReferenceComponent).vehicle?.slug}/${slug}`;
  }

  if (type === DocumentTypeLabel.nafMembershipBenefit) {
    return `/medlemskap/medlemsfordeler/${slug}`;
  }

  if (type === DocumentTypeLabel.nafCenterProductArticle) {
    // Only nest under the parent slug when it's another product - a category/site-structure
    // parent shares the base path and would otherwise double it up (e.g. .../bilverksted-og-tester/bilverksted-og-tester/...).
    if (documentData.parent?.type === DocumentTypeLabel.nafCenterProductArticle && documentData.parent?.slug) {
      return `/bilverksted-og-tester/${documentData.parent.slug}/${slug}`;
    }
    return `/bilverksted-og-tester/${slug}`;
  }

  if (type === DocumentTypeLabel.topicPage || type === DocumentTypeLabel.productPage) {
    if (documentData.category) {
      return resolveCategoryUrl(documentData as TopicPageAppPropertiesType, categoryData);
    }
    if (documentData.parent?.type === DocumentTypeLabel.nafnoSiteStructure && documentData.parent?.slug) {
      return `${categoryData[documentData.parent.slug]?.data?.url || ''}/${slug}`;
    }
    return `/${slug}`;
  }

  if (type === DocumentTypeLabel.nafnoProductArticle) {
    if (slug === 'medlemskap') {
      return '/medlemskap';
    }
    if (documentData.parent?.type === DocumentTypeLabel.nafnoProductArticle) {
      return `/${documentData.parent.slug}/${slug}`;
    }
    return `/${slug}`;
  }

  if (type === DocumentTypeLabel.testReferenceComponent) {
    return resolveCategoryUrl(documentData as TestReferenceComponent, categoryData);
  }

  if (type === DocumentTypeLabel.nafnoSiteStructure) {
    return categoryData[slug]?.data?.url;
  }

  // Fallback: resolve via vehicle slug if present
  if ((documentData as any).vehicle?.slug) {
    return `/bilguiden/bilmodell/${(documentData as any).vehicle.slug}`;
  }

  return undefined;
};

export const appendUrlModifiers = (
  baseUrl: string | undefined,
  queryParams?: QueryParam[],
  anchorId?: string,
): string | undefined => {
  if (!baseUrl) {
    return undefined;
  }

  let url = baseUrl;

  if (queryParams && queryParams.length > 0) {
    const params = new URLSearchParams();
    queryParams.forEach(({ key, value }) => params.append(key, value));
    url = `${url}?${params.toString()}`;
  }

  if (anchorId) {
    url = `${url}#${anchorId}`;
  }

  return url;
};
